import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useGetAdminWorkflowExecutionsQuery } from "@/store/admin_api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { format } from "date-fns";
import SystemWorkflowsCard from "@/components/pages/platform-admin/organisations/SystemWorkflowsCard";

const RUNS_PER_PAGE = 10;

const RUN_STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  RUNNING: "default",
  COMPLETED: "secondary",
  FAILED: "destructive",
  CANCELLED: "outline",
};

interface OrganisationWorkflowsTabProps {
  organisationId: string;
}

export default function OrganisationWorkflowsTab({ organisationId }: OrganisationWorkflowsTabProps) {
  const [page, setPage] = useState(0);
  const { data: runs, isFetching } = useGetAdminWorkflowExecutionsQuery({
    organisationId,
    page,
    size: RUNS_PER_PAGE,
  });

  const totalPages = runs?.totalPages ?? 0;

  return (
    <div className="space-y-6">
      <SystemWorkflowsCard organisationId={organisationId} />

      <Card>
        <CardHeader>
          <CardTitle>Recent workflow runs</CardTitle>
          <CardDescription>Every workflow this organisation has run, newest first.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isFetching && <Skeleton className="h-64 w-full" />}

          {!isFetching && runs?.items.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">This organisation has not run any workflows yet.</p>
          )}

          {!isFetching && runs && runs.items.length > 0 && (
            <div className="divide-y rounded-lg border">
              {runs.items.map((run) => (
                <Link
                  key={run.id}
                  to={`/platform-admin/workflow-runs/${run.id}`}
                  className="flex items-start justify-between gap-4 p-4 hover:bg-muted/50"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{run.workflow?.name ?? "Deleted workflow"}</p>
                    <p className="text-sm text-muted-foreground">
                      {run.triggeredAt ? format(new Date(run.triggeredAt), "d MMM yyyy, HH:mm") : "Not started"}
                    </p>
                    {run.error && <p className="text-sm text-destructive">{run.error}</p>}
                  </div>
                  <Badge variant={RUN_STATUS_VARIANTS[run.status] ?? "outline"} className="shrink-0">
                    {run.status}
                  </Badge>
                </Link>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground tabular-nums">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((previous) => Math.max(0, previous - 1))}
                  disabled={page === 0 || isFetching}
                >
                  <FaChevronLeft className="mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((previous) => previous + 1)}
                  disabled={page + 1 >= totalPages || isFetching}
                >
                  Next
                  <FaChevronRight className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
